/**
 * ScheduleHintLine — answers "when can this project start, and what's
 * holding it up?" inline on the Team card.
 *
 * The Planning page already answers this question — but it lives on a
 * separate route the user has to navigate to, find their project in,
 * and read across columns. The same answer belongs next to the team
 * roster, because that's where users naturally ask the question.
 *
 * Renders nothing when:
 *   - The scheduler can't compute a date (no roles / no hours)
 *   - The project is already in flight or terminal (caller should
 *     gate via PRE_START_STATUSES rather than relying on us to)
 *
 * Visual modes:
 *   - "ready now"     — emerald, "Ready to start now"
 *   - "queued"        — sky,     "Earliest start: <date> · gated by <role>"
 *   - "infeasible"    — red,     "Can't be scheduled in the current horizon"
 */

import { CalendarClock, CheckCircle2, AlertOctagon, Calculator } from "lucide-react";
import { cn } from "@/lib/cn";
import { formatDate } from "@/lib/format";
import { useExplainProject } from "@/hooks/useExplain";
import { DIRECT_ROLE_LABEL } from "@/features/direct/constants";

interface Props {
  projectId: string;
  /** If true, render a "View calculation" affordance that links to
   *  Planning. Useful from the Team card; less useful when the line
   *  is itself inside a calculation drawer. */
  showCalcLink?: boolean;
}

export function ScheduleHintLine({ projectId, showCalcLink = true }: Props) {
  const { data, isLoading, isError } = useExplainProject(projectId);

  if (isLoading) {
    return (
      <div className="rounded-md border border-dashed border-line bg-canvas px-3 py-2 text-xs text-ink-subtle">
        Computing earliest start…
      </div>
    );
  }
  if (isError || !data) return null;

  // Stub responses come back with no suggested_start (the project has
  // no hours or no role allocations). Nothing useful to show.
  if (data.suggested_start === undefined) return null;

  const readyNow = data.wait_weeks === 0 && !!data.suggested_start;
  const infeasible = data.suggested_start === null;
  const queued = !readyNow && !infeasible && !!data.suggested_start;

  if (!readyNow && !queued && !infeasible) return null;

  const bottleneckLabel = data.bottleneck_role
    ? DIRECT_ROLE_LABEL[data.bottleneck_role] ?? data.bottleneck_role
    : null;

  return (
    <div
      className={cn(
        "rounded-md border px-3 py-2 text-xs flex items-center gap-2",
        readyNow && "border-emerald-200 dark:border-emerald-500/30 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-800 dark:text-emerald-300",
        queued && "border-sky-200 dark:border-sky-500/30 bg-sky-50 dark:bg-sky-500/10 text-sky-800 dark:text-sky-300",
        infeasible && "border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 text-red-800 dark:text-red-300",
      )}
    >
      {readyNow && <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />}
      {queued && <CalendarClock className="h-3.5 w-3.5 shrink-0" />}
      {infeasible && <AlertOctagon className="h-3.5 w-3.5 shrink-0" />}

      <div className="flex-1 min-w-0">
        {readyNow && (
          <span>
            <strong className="font-semibold">Ready to start now</strong>
            {" — "}capacity covers it.
          </span>
        )}
        {queued && (
          <span>
            <strong className="font-semibold">
              Earliest start: {formatDate(data.suggested_start)}
            </strong>
            {data.wait_weeks != null && data.wait_weeks > 0 && (
              <> · {data.wait_weeks}w wait</>
            )}
            {bottleneckLabel && <> · gated by {bottleneckLabel}</>}
          </span>
        )}
        {infeasible && (
          <span>
            <strong className="font-semibold">Can't be scheduled</strong>
            {" in the current horizon"}
            {bottleneckLabel && <> · bottleneck: {bottleneckLabel}</>}
          </span>
        )}
      </div>

      {/* "Open in Planning" calc link retired with the /planning page
          (2026-09-04) — the hint line itself carries the verdict. */}
    </div>
  );
}
